import { MoreHorizontal, Plus, Star } from 'lucide-react'
import { useRef, useState } from 'react'
import { Link } from 'react-router'
import { DataTable, type DataTableColumn } from '@/components/patterns/DataTable'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { EntityAvatar } from '@/components/ui/entity-avatar'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { toast } from '@/lib/toast'
import { toastUndo } from '@/lib/toastUndo'
import { cn } from '@/lib/utils'
import { todayIso } from '../dates'
import { removeMember, restoreMember, updateMember } from './api'
import type { CrmMember, CrmMemberList, CrmOrganisation } from './types'

const menuItemClass =
  'rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted'

/**
 * The table is wider than the middle column of the record page at most desktop widths,
 * so it scrolls sideways; the actions stay pinned to the right edge. The cell needs its
 * own background to hide what scrolls under it, and takes over the row's hover tint.
 */
const pinnedActionsClass =
  'sticky right-0 w-10 bg-card [tr:hover>&]:bg-[color-mix(in_oklab,var(--color-muted)_50%,var(--color-card))]'

function errorText(caught: unknown, fallback: string): string {
  return caught instanceof Error ? caught.message : fallback
}

/**
 * The actions of one row. The menu closes when an item is chosen (and Radix puts the
 * focus back on its button); the role dialog hands the focus back to that button too,
 * because the menu item that opened it is gone by then.
 */
function MemberMenu({
  member,
  organisation,
  busy,
  run,
  unlink,
}: {
  member: CrmMember
  organisation: CrmOrganisation
  busy: boolean
  run: (action: () => Promise<unknown>, success: string) => Promise<boolean>
  unlink: (member: CrmMember) => Promise<void>
}) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [roleOpen, setRoleOpen] = useState(false)
  const [role, setRole] = useState('')
  const trigger = useRef<HTMLButtonElement>(null)

  function choose(action: () => void) {
    setMenuOpen(false)
    action()
  }

  function makePrimary() {
    void run(
      () => updateMember(member.id, { is_primary: true }),
      `${organisation.name} is nu de primaire organisatie van ${member.full_name}.`,
    )
  }

  return (
    <>
      <Popover open={menuOpen} onOpenChange={setMenuOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={trigger}
            type="button"
            size="icon-sm"
            variant="ghost"
            disabled={busy}
            aria-label={`Acties voor ${member.full_name}`}
          >
            <MoreHorizontal />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="flex w-52 flex-col p-1">
          <button
            type="button"
            className={menuItemClass}
            onClick={() =>
              choose(() => {
                setRole(member.role ?? '')
                setRoleOpen(true)
              })
            }
          >
            Rol wijzigen
          </button>
          {!member.ended_on && !member.is_primary ? (
            <button type="button" className={menuItemClass} onClick={() => choose(makePrimary)}>
              Primair maken
            </button>
          ) : null}
          {member.ended_on ? (
            <button
              type="button"
              className={menuItemClass}
              onClick={() =>
                choose(() =>
                  void run(
                    () => updateMember(member.id, { ended_on: null }),
                    `${member.full_name} is weer contactpersoon.`,
                  ),
                )
              }
            >
              Weer huidig maken
            </button>
          ) : (
            <button
              type="button"
              className={menuItemClass}
              onClick={() =>
                choose(() =>
                  void run(
                    () => updateMember(member.id, { ended_on: todayIso() }),
                    `${member.full_name} is nu voormalig contactpersoon.`,
                  ),
                )
              }
            >
              Voormalig maken
            </button>
          )}
          <button
            type="button"
            className={cn(menuItemClass, 'text-destructive')}
            onClick={() => choose(() => void unlink(member))}
          >
            Ontkoppelen
          </button>
        </PopoverContent>
      </Popover>
      <Dialog open={roleOpen} onOpenChange={setRoleOpen}>
        <DialogContent
          size="sm"
          aria-describedby={undefined}
          onCloseAutoFocus={(event) => {
            event.preventDefault()
            trigger.current?.focus()
          }}
        >
          <DialogHeader>
            <DialogTitle>Rol van {member.full_name}</DialogTitle>
          </DialogHeader>
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault()
              // Closes on success only: after a failure the typed role is still there.
              void run(
                () => updateMember(member.id, { role: role.trim() || null }),
                'Rol opgeslagen.',
              ).then((saved) => {
                if (saved) setRoleOpen(false)
              })
            }}
          >
            <Input value={role} onChange={(event) => setRole(event.target.value)} aria-label="Rol" />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setRoleOpen(false)}>
                Annuleren
              </Button>
              <Button type="submit" disabled={busy}>
                Opslaan
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  )
}

/**
 * Tab "Contactpersonen" (design §4.4): role, primary (click the star), e-mail, phone,
 * owner; row actions Rol wijzigen · Primair maken · Voormalig maken · Ontkoppelen (with
 * undo). Former members only with "Toon voormalige". No "since" column in E1: no screen
 * sets `started_on` yet, so it would only ever show a dash.
 */
export function MembersTab({
  organisation,
  members,
  onChanged,
  onAdd,
}: {
  organisation: CrmOrganisation
  members: CrmMemberList
  onChanged: () => void
  onAdd: () => void
}) {
  const [showFormer, setShowFormer] = useState(false)
  // One change at a time: while one is on its way the buttons that start one are disabled.
  const [busy, setBusy] = useState(false)
  const rows = members.items.filter((member) => showFormer || !member.ended_on)

  /** Runs one change and reports it; true when it went through. */
  async function run(action: () => Promise<unknown>, success: string): Promise<boolean> {
    setBusy(true)
    try {
      await action()
      toast.success(success)
      onChanged()
      return true
    } catch (caught) {
      toast.error(errorText(caught, 'Opslaan mislukt.'))
      return false
    } finally {
      setBusy(false)
    }
  }

  async function unlink(member: CrmMember) {
    try {
      await removeMember(member.id)
      onChanged()
      toastUndo(`${member.full_name} ontkoppeld van ${organisation.name}.`, async () => {
        await restoreMember(member.id)
        onChanged()
      })
    } catch (caught) {
      toast.error(errorText(caught, 'Ontkoppelen mislukt.'))
    }
  }

  const columns: DataTableColumn<CrmMember>[] = [
    {
      key: 'name',
      header: 'Naam',
      cell: (member) => (
        <span className={cn('flex items-center gap-2', member.ended_on && 'text-muted-foreground')}>
          <EntityAvatar kind="person" name={member.full_name} size="sm" />
          <Link
            to={`/relaties/contacten/${member.person_id}`}
            className="font-medium underline-offset-2 hover:underline"
          >
            {member.full_name}
          </Link>
          {member.ended_on ? <span className="text-xs">(voormalig)</span> : null}
        </span>
      ),
    },
    { key: 'role', header: 'Rol', cell: (member) => member.role ?? '—' },
    {
      key: 'primary',
      header: 'Primair',
      cell: (member) =>
        member.is_primary ? (
          <span className="inline-flex items-center gap-1 text-sm">
            <Star className="size-4 fill-amber-400 text-amber-500" aria-hidden />
            primair
          </span>
        ) : member.ended_on ? null : (
          <button
            type="button"
            aria-label={`${member.full_name}: primair maken`}
            title={`Maak ${organisation.name} de primaire organisatie van ${member.full_name}`}
            disabled={busy}
            onClick={() =>
              void run(
                () => updateMember(member.id, { is_primary: true }),
                `${organisation.name} is nu de primaire organisatie van ${member.full_name}.`,
              )
            }
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
          >
            <Star className="size-4" aria-hidden />
          </button>
        ),
    },
    {
      key: 'email',
      header: 'E-mail',
      cell: (member) =>
        member.email ? (
          <a
            href={`mailto:${member.email}`}
            title={member.email}
            className="block max-w-40 truncate underline-offset-2 hover:underline"
          >
            {member.email}
          </a>
        ) : (
          '—'
        ),
    },
    { key: 'phone', header: 'Telefoon', cell: (member) => member.phone ?? '—' },
    { key: 'owner', header: 'Eigenaar', cell: (member) => member.owner?.full_name ?? '—' },
    {
      key: 'actions',
      header: <span className="sr-only">Acties</span>,
      className: pinnedActionsClass,
      cell: (member) => (
        <MemberMenu member={member} organisation={organisation} busy={busy} run={run} unlink={unlink} />
      ),
    },
  ]

  return (
    <section aria-label="Contactpersonen" className="vonk-card flex flex-col gap-4 p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            className="size-4 accent-primary"
            checked={showFormer}
            onChange={(event) => setShowFormer(event.target.checked)}
          />
          Toon voormalige ({members.former_count})
        </label>
        <Button type="button" onClick={onAdd}>
          <Plus />
          Contactpersoon
        </Button>
      </div>
      <DataTable
        caption={`Contactpersonen van ${organisation.name}`}
        columns={columns}
        rows={rows}
        rowKey={(member) => member.id}
        empty={<p className="py-4 text-center text-sm text-muted-foreground">Nog geen contactpersonen.</p>}
      />
    </section>
  )
}
